/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {Injectable} from '@angular/core';

import {forkJoin, NEVER, Observable, of} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';

import * as moment from 'moment';

import {LeaveRequestData} from '../../../+ess/employee-self.service';
import {ProfileMode, REQUEST_OPTIONS} from '../../../app.constants';
import {ApiService} from '../../../shared/api';
import {ProcessInstance} from '../../../shared/workflow/workflow-v2.interface';
import {
  ESS_ABSENCE_WORKFLOW,
  getWorkflowKey,
  HR_ABSENCE_WORKFLOW,
  WorkflowV2Service
} from '../../../shared/workflow/workflow-v2.service';

export interface EstimateAbsenceDeltasData {
  ranges: Array<{ beginDate: string, endDate: string }>;
  uiEvents: Array<{
    beginDate: string,
    beginDayFactor: string,
    endDate: string,
    endDayFactor: string,
    creationDate: string,
    eventType: string,
    absenceType: string
  }>;
}

const mapRequest = item => ({
  transId: item.attributes.absenceType,
  begin: item.attributes.beginDate,
  end: item.attributes.endDate,
  beginDayfactor: item.attributes.beginDateFactor,
  endDayfactor: item.attributes.endDateFactor,
  recipientName: item.attributes.recipientName,
  dateCreated: item.attributes.dateCreated,
  onCancellation: item.attributes.onCancellation,
  empAbsenceId: item.attributes.empAbsenceId
});

const mapWorkflow = item => ({
  transId: item.transId,
  begin: item.beginDate,
  end: item.endDate,
  beginDayfactor: item.beginDateFactor,
  endDayfactor: item.endDateFactor,
  recipientName: item.recipientName,
  dateCreated: item.dateCreated
});

const mapAbsence = item => ({
  id: item.id,
  type: 'empAbsence',
  attributes: {
    begin: item.beginDate,
    beginDayfactor: item.beginDayfactor,
    cliCoreID: item.cliCoreID,
    empContract: item.empContractId,
    end: item.endDate,
    endDayfactor: item.endDayfactor,
    name: item.name,
    transid: item.transId,
    status: item.status,
    empAbsenceId: item.empAbsenceId
    // updatedate: "2018-09-24T13:32:10.456+0000"
  }
});

/**
 * Time and Attendance service
 */
@Injectable()
export class TimeAndAttendanceService {

  constructor(private api: ApiService, private workflowV2Service: WorkflowV2Service) {
  }

  public getWorkCycle(contractId: number, periodBegin: string, periodEnd: string, profileMode: ProfileMode): Observable<any> {
    return this.api.get(`/ga/api/v1/empContract/${contractId}/workcycle?profile-mode=${profileMode}`, {
      requestOptions: {
        params: {
          periodBegin,
          periodEnd
        }
      }
    });
  }

  public getPresenceAndAbsenceTypes(cliReg: number, isEss: boolean = false): Observable<any> {

    if (isEss) {
      return this.api.get(`/repository/api/v1/mySpace/${cliReg}/mySpaceEvAbsenceType`);
    }

    return this.api.get(`/repository/api/v1/cliReg/${cliReg}/EvAbsenceType`);
  }

  public getTAListForMDU(): Observable<any> {
    return this.api.get(`/repository/api/v1/cliReg/timeAndAttendance`);
  }

  public getRepReport(id: number, reportName: string): Observable<any> {
    return this.api.get(`/repository/api/v1/cliReg/${id}/report/${reportName}`);
  }

  public getLastAccus(id: number, search?: string): Observable<any> {
    return this.api.get(`/payroll/api/v1/empContract/${id}/lastAccus?search=&${search}`);
  }

  public getLastClosedOnlyAccus(id: number, names?: string): Observable<any> {
    return this.api.get(`/payroll/api/v1/empContract/${id}/lastClosedOnlyAccus?${names}`);
  }

  public getLastClosedPayPeriod(id: number): Observable<any> {
    return this.api.get(`/payroll/api/v1/payPeriod/lastClosedForContract/${id}`);
  }

  public estimateAbsenceDeltas(contractId: number, absenceType: string, maxModificationDateTime: string,
                               minAbsenceBeginDate: string, data: EstimateAbsenceDeltasData): Observable<any> {
    return this.api.post(`/ga/api/v1/empAbsence/estimateAbsenceDeltas/${contractId}`, data, {
      requestOptions: {
        params: {
          absenceType,
          maxModificationDateTime,
          minAbsenceBeginDate
        }
      }
    });
  }

  public getAbsenceDuration(contractId: number, beginDate: string, beginDayFactor: number, endDate: string, endDayFactor: number,
                            absenceType: string): Observable<any> {
    return this.api.post(`/ga/api/v1/empAbsence/getAbsenceDuration/${contractId}`, {
      beginDate: beginDate,
      beginDayFactor: beginDayFactor,
      endDate: endDate,
      endDayFactor: endDayFactor,
      absenceType: absenceType
    });
  }

  public getEmployeeAbsence(
    contractId: number,
    periodBegin: string,
    periodEnd: string,
    isEss: boolean = false
  ): Observable<any> {

    return this.api.get(`/ga/api/v1/empContract/${contractId}/absence`, {
      requestOptions: {
        params: {
          periodBegin,
          periodEnd,
          isEss
        }
      }
    });
  }

  public getPendingAbsences(contractId: number, periodBegin: string, periodEnd: string): Observable<any> {
    return this.api.get(`/ga/api/v1/empAbsence/getPendingAbsences/${contractId}`, {
      requestOptions: {
        params: {
          periodBegin,
          periodEnd
        }
      }
    })
      .pipe(
        map(res => res || ({data: []})),
        map(data => data.map(mapAbsence))
      );
  }

  // TODO: usage `types``
  public getPendingRequests(contractId: number, types?: any) {
    return this.getAllPendingRequests(contractId);
  }

  private getAllPendingRequests(contractId: number) {
    return forkJoin(
      this.api.get(`/ga/api/v1/workflow/getPendingRequests/${contractId}`),
      this.workflowV2Service.getPendingRequests(contractId)
    )
      .pipe(
        map(([pendingRequests, workflowPendingRequests]) => {
          const pendingRequestsV1 = pendingRequests.data.map(mapRequest);
          const pendingRequestsV2 = workflowPendingRequests.map(mapWorkflow);

          return pendingRequestsV1
            .concat(pendingRequestsV2)
            .sort((a, b) => moment(b.dateCreated).diff(moment(a.dateCreated)));
        })
      );
  }

  public updateAbsence(data: LeaveRequestData): Observable<any> {
    // return this.api.post(`/ga/api/v1/empAbsence/${data.id}`, data.attributes);
    return this.api.post(`/ga/api/v1/workflow/updateAbsence`, data);
  }

  public createAbsence(data: LeaveRequestData): Observable<Response> {
    // return this.api.post('/ga/api/v1/empAbsence/', data['attributes']);
    return this.api.post(`/ga/api/v1/workflow/askAbsence`, data);
  }

  public triggerAbsenceWorkflowV2(
    absenceVariables: LeaveRequestData,
    cliRegId: number,
    action: string,
    isEss = false
  ): Observable<ProcessInstance> {
    const workflowName = isEss ? ESS_ABSENCE_WORKFLOW : HR_ABSENCE_WORKFLOW;
    const workflowKey = getWorkflowKey(workflowName, cliRegId);
    return this.workflowV2Service.startProcessInstance(workflowKey, {
      request: absenceVariables,
      absenceType: absenceVariables.name,
      action: action
    }, false, isEss ? ProfileMode.ESS : ProfileMode.CLASSIC);
  }

  public deleteAbsence(data: LeaveRequestData): Observable<any> {
    return this.api.post(`/ga/api/v1/workflow/cancelAbsence`, data);
  }

  public getAccrualData(cliRegId: number, absenceType: string, profileMode: ProfileMode) {
    return this.api.get(`/repository/api/v1/cliReg/${cliRegId}/report/${absenceType}?profile-mode=${profileMode}`, REQUEST_OPTIONS.OBSERVE.RESPONSE)
      .pipe(
        switchMap((response) => {
          if (response.status === 204) {
            return of(null);
          }
          return of(response.body);
        })
      );
  }

  public getRepAbsenceZones(cliRegId: number, absenceType: string, isEss: boolean = false) {
    return this.api.config
      .get(`/cliReg/${cliRegId}/repAbsenceZones/${absenceType}?isEss=${isEss}`, REQUEST_OPTIONS.OBSERVE.RESPONSE)
      .pipe(
        switchMap((response) => {
          if (response.status === 204) {
            return NEVER;
          }
          return of(response.body);
        })
      );
  }
}
